/**
 * Google Calendar Sync Service
 * 
 * Handles pushing AX1 calendar events to Google Calendar.
 * 
 * Sync Principles:
 * - AX1 is the single source of truth
 * - Create or update events only (never delete automatically)
 * - Uses dedicated "AX1 – Work" calendar
 * - Syncs meetings and milestones only
 * - All operations are logged for audit
 * 
 * @module GoogleCalendarSyncService
 */

import type { CalendarEvent, ExternalSyncInfo } from '../types';
import type { AsyncResult, CalendarConnectionError, CalendarErrorCode } from './types';
import { GoogleCalendarAuthService } from './GoogleCalendarAuthService';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Name of the dedicated AX1 calendar in Google Calendar.
 */
const AX1_CALENDAR_NAME = 'AX1 – Work';

/**
 * Description for the AX1 calendar.
 */
const AX1_CALENDAR_DESCRIPTION = 'Events synced from AX1 | Capital project management platform. Do not edit events here - changes should be made in AX1.';

/**
 * Event types that are eligible for sync to Google Calendar.
 */
const SYNCABLE_EVENT_TYPES: CalendarEvent['type'][] = ['meeting', 'milestone'];

/**
 * Google Calendar API base URL.
 */
const GOOGLE_CALENDAR_API = 'https://www.googleapis.com/calendar/v3';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Result of a sync operation for a single event.
 */
export interface SyncEventResult {
  /** AX1 event ID */
  ax1EventId: string;
  
  /** Google event ID (if created/updated) */
  googleEventId?: string;
  
  /** Operation performed */
  operation: 'created' | 'updated' | 'skipped' | 'error';
  
  /** Reason for skip or error details */
  message?: string;
  
  /** Updated sync info to persist on the AX1 event */
  syncInfo?: ExternalSyncInfo;
  
  /** Timestamp of sync attempt */
  timestamp: string;
}

/**
 * Result of a batch sync operation.
 */
export interface BatchSyncResult {
  /** Total events processed */
  total: number;
  
  /** Successfully synced events */
  created: number;
  
  /** Successfully updated events */
  updated: number;
  
  /** Skipped events (not eligible or already synced) */
  skipped: number;
  
  /** Failed events */
  errors: number;
  
  /** Individual results for each event */
  results: SyncEventResult[];
  
  /** AX1 calendar ID in Google */
  ax1CalendarId?: string;
  
  /** Sync start timestamp */
  startedAt: string;
  
  /** Sync end timestamp */
  completedAt: string;
}

/**
 * Google Calendar event resource (subset of fields we use).
 */
interface GoogleCalendarEvent {
  id?: string;
  summary: string;
  description?: string;
  location?: string;
  start: {
    dateTime?: string;
    date?: string;
    timeZone?: string;
  };
  end: {
    dateTime?: string;
    date?: string;
    timeZone?: string;
  };
  attendees?: Array<{ email: string; displayName?: string }>;
  status?: 'confirmed' | 'tentative' | 'cancelled';
  visibility?: 'default' | 'public' | 'private';
  extendedProperties?: {
    private?: Record<string, string>;
  };
  updated?: string;
}

/**
 * Google Calendar list entry (subset of fields).
 */
interface GoogleCalendarListEntry {
  id: string;
  summary: string;
  description?: string;
  primary?: boolean;
  accessRole: string;
}

/**
 * Sync log entry for audit trail.
 */
export interface SyncLogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error';
  operation: string;
  eventId?: string;
  googleEventId?: string;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Type of conflict detected during pull operation.
 */
export type ConflictType = 
  | 'title_changed'
  | 'time_changed'
  | 'event_deleted'
  | 'event_cancelled'
  | 'protected_field_changed'
  | 'unknown_event';

/**
 * A detected conflict between Google and AX1 event.
 */
export interface SyncConflict {
  /** Type of conflict */
  type: ConflictType;
  
  /** AX1 event ID */
  ax1EventId: string;
  
  /** Google event ID */
  googleEventId: string;
  
  /** Field that has conflict (if applicable) */
  field?: string;
  
  /** Value in AX1 */
  ax1Value?: unknown;
  
  /** Value in Google */
  googleValue?: unknown;
  
  /** Human-readable description */
  description: string;
  
  /** Recommended action */
  recommendedAction: 'accept' | 'reject' | 'review';
  
  /** Whether conflict was auto-resolved */
  autoResolved: boolean;
  
  /** Timestamp of detection */
  detectedAt: string;
}

/**
 * Result of pulling changes from a single Google event.
 */
export interface PullEventResult {
  /** AX1 event ID */
  ax1EventId: string;
  
  /** Google event ID */
  googleEventId: string;
  
  /** Operation performed */
  operation: 'updated' | 'cancelled' | 'skipped' | 'conflict' | 'error';
  
  /** Fields that were updated (if operation is 'updated') */
  updatedFields?: string[];
  
  /** Conflicts detected */
  conflicts?: SyncConflict[];
  
  /** Partial update to apply to AX1 event (only allowed fields) */
  partialUpdate?: Partial<CalendarEvent>;
  
  /** Message describing the result */
  message?: string;
  
  /** Timestamp */
  timestamp: string;
}

/**
 * Result of a batch pull operation.
 */
export interface PullChangesResult {
  /** Total events checked */
  total: number;
  
  /** Events with updates applied */
  updated: number;
  
  /** Events marked as cancelled */
  cancelled: number;
  
  /** Events skipped (no changes) */
  skipped: number;
  
  /** Events with conflicts requiring review */
  conflicts: number;
  
  /** Errors encountered */
  errors: number;
  
  /** Individual results */
  results: PullEventResult[];
  
  /** All conflicts detected (for review) */
  allConflicts: SyncConflict[];
  
  /** Timestamp of pull start */
  startedAt: string;
  
  /** Timestamp of pull completion */
  completedAt: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a structured error object.
 */
function createError(
  code: CalendarErrorCode,
  message: string,
  details?: string,
  recoverable = true,
  suggestedAction?: string
): CalendarConnectionError {
  return {
    code,
    message,
    details,
    recoverable,
    suggestedAction,
    occurredAt: new Date().toISOString(),
  };
}

/**
 * Checks if an event type is eligible for sync.
 */
function isEventSyncable(event: CalendarEvent): boolean {
  return SYNCABLE_EVENT_TYPES.includes(event.type);
}

/**
 * Converts AX1 event to Google Calendar event format.
 */
function convertToGoogleEvent(event: CalendarEvent, ax1EventId: string): GoogleCalendarEvent {
  const eventDate = new Date(event.date);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Build start and end times
  let start: GoogleCalendarEvent['start'];
  let end: GoogleCalendarEvent['end'];
  
  if (event.time && event.duration) {
    // Timed event with duration
    const [hours, minutes] = event.time.split(':').map(Number);
    const startDate = new Date(eventDate);
    startDate.setHours(hours, minutes, 0, 0);
    
    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + event.duration);
    
    start = { dateTime: startDate.toISOString(), timeZone };
    end = { dateTime: endDate.toISOString(), timeZone };
  } else if (event.time) {
    // Timed event without duration (default 1 hour)
    const [hours, minutes] = event.time.split(':').map(Number);
    const startDate = new Date(eventDate);
    startDate.setHours(hours, minutes, 0, 0);
    
    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + 1);
    
    start = { dateTime: startDate.toISOString(), timeZone };
    end = { dateTime: endDate.toISOString(), timeZone };
  } else {
    // All-day event (milestones typically)
    const dateString = eventDate.toISOString().split('T')[0];
    const nextDay = new Date(eventDate);
    nextDay.setDate(nextDay.getDate() + 1);
    const endDateString = nextDay.toISOString().split('T')[0];
    
    start = { date: dateString };
    end = { date: endDateString };
  }
  
  // Build description with AX1 metadata
  const descriptionParts: string[] = [];
  if (event.description) {
    descriptionParts.push(event.description);
  }
  descriptionParts.push('');
  descriptionParts.push('─────────────────────────');
  descriptionParts.push(`📌 Synced from AX1 | Capital`);
  descriptionParts.push(`Type: ${event.type.charAt(0).toUpperCase() + event.type.slice(1)}`);
  if (event.project) {
    descriptionParts.push(`Project: ${event.project}`);
  }
  if (event.priority) {
    descriptionParts.push(`Priority: ${event.priority}`);
  }
  if (event.status) {
    descriptionParts.push(`Status: ${event.status}`);
  }
  descriptionParts.push('');
  descriptionParts.push('⚠️ Do not edit this event here. Make changes in AX1.');
  
  // Build title with type indicator
  const typeEmoji = event.type === 'meeting' ? '📅' : '🎯';
  const summary = `${typeEmoji} ${event.title}`;
  
  // Convert attendees if present
  const attendees = event.attendees?.map(name => ({
    displayName: name,
    email: '', // We don't have emails, just names
  }));
  
  // Map AX1 status to Google status
  let status: GoogleCalendarEvent['status'] = 'confirmed';
  if (event.status === 'cancelled') {
    status = 'cancelled';
  }
  
  return {
    summary,
    description: descriptionParts.join('\n'),
    location: event.location,
    start,
    end,
    attendees: attendees?.length ? attendees : undefined,
    status,
    visibility: 'default',
    extendedProperties: {
      private: {
        ax1EventId,
        ax1EventType: event.type,
        ax1ProjectId: event.project || '',
        ax1SyncedAt: new Date().toISOString(),
      },
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Google Calendar Sync Service Class
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Service for syncing AX1 events to Google Calendar.
 * 
 * Usage:
 * ```typescript
 * const syncService = GoogleCalendarSyncService.getInstance();
 * const result = await syncService.pushEventToGoogle(ax1Event);
 * if (result.success) {
 *   // Update AX1 event with result.data.syncInfo
 * }
 * ```
 */
export class GoogleCalendarSyncService {
  private static instance: GoogleCalendarSyncService | null = null;
  
  // Auth service for token management
  private authService: GoogleCalendarAuthService;
  
  // Cached AX1 calendar ID
  private ax1CalendarId: string | null = null;
  
  // Sync log (in-memory, could be persisted)
  private syncLog: SyncLogEntry[] = [];
  
  // Maximum log entries to keep
  private readonly MAX_LOG_ENTRIES = 1000;

  // ─────────────────────────────────────────────────────────────────────────
  // Constructor & Singleton
  // ─────────────────────────────────────────────────────────────────────────

  private constructor() {
    this.authService = GoogleCalendarAuthService.getInstance();
  }

  /**
   * Get singleton instance of the service.
   */
  public static getInstance(): GoogleCalendarSyncService {
    if (!GoogleCalendarSyncService.instance) {
      GoogleCalendarSyncService.instance = new GoogleCalendarSyncService();
    }
    return GoogleCalendarSyncService.instance;
  }

  /**
   * Reset singleton instance (for testing purposes).
   */
  public static resetInstance(): void {
    GoogleCalendarSyncService.instance = null;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: Event Sync
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Pushes a single AX1 event to Google Calendar.
   * Creates a new event or updates an existing one.
   * 
   * @param event - AX1 calendar event to sync
   * @returns Sync result with updated sync info
   */
  public async pushEventToGoogle(
    event: CalendarEvent
  ): Promise<AsyncResult<SyncEventResult>> {
    const timestamp = new Date().toISOString();
    
    try {
      // Check if event type is eligible for sync
      if (!isEventSyncable(event)) {
        const result: SyncEventResult = {
          ax1EventId: event.id,
          operation: 'skipped',
          message: `Event type '${event.type}' is not eligible for sync. Only meetings and milestones are synced.`,
          timestamp,
        };
        
        this.log('info', 'pushEventToGoogle', event.id, undefined, result.message);
        
        return { success: true, data: result };
      }
      
      // Ensure we're connected and have a valid token
      const tokenResult = await this.authService.getValidAccessToken();
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }
      
      // Ensure AX1 calendar exists
      const calendarResult = await this.ensureAx1Calendar(tokenResult.data);
      if (!calendarResult.success) {
        return { success: false, error: calendarResult.error };
      }
      
      const calendarId = calendarResult.data;
      
      // Check if event already has a Google event ID (update vs create)
      const existingGoogleEventId = event.externalSync?.provider === 'google' 
        ? event.externalSync.externalEventId 
        : undefined;
      
      let result: SyncEventResult;
      
      if (existingGoogleEventId) {
        // Update existing event
        result = await this.updateGoogleEventInternal(
          event,
          calendarId,
          existingGoogleEventId,
          tokenResult.data
        );
      } else {
        // Create new event
        result = await this.createGoogleEventInternal(
          event,
          calendarId,
          tokenResult.data
        );
      }
      
      return { success: true, data: result };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      this.log('error', 'pushEventToGoogle', event.id, undefined, errorMessage);
      
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Failed to sync event to Google Calendar',
          errorMessage,
          true,
          'Please try again'
        ),
      };
    }
  }

  /**
   * Updates an existing Google Calendar event.
   * 
   * @param event - AX1 calendar event with updated data
   * @returns Sync result
   */
  public async updateGoogleEvent(
    event: CalendarEvent
  ): Promise<AsyncResult<SyncEventResult>> {
    // Delegate to pushEventToGoogle which handles both create and update
    return this.pushEventToGoogle(event);
  }

  /**
   * Syncs multiple AX1 events to Google Calendar.
   * 
   * @param events - Array of AX1 events to sync
   * @returns Batch sync result
   */
  public async syncEventsToGoogle(
    events: CalendarEvent[]
  ): Promise<AsyncResult<BatchSyncResult>> {
    const startedAt = new Date().toISOString();
    const results: SyncEventResult[] = [];
    
    let created = 0;
    let updated = 0;
    let skipped = 0;
    let errors = 0;
    
    this.log('info', 'syncEventsToGoogle', undefined, undefined, 
      `Starting batch sync of ${events.length} events`);
    
    try {
      // Ensure we're connected
      const tokenResult = await this.authService.getValidAccessToken();
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }
      
      // Ensure AX1 calendar exists
      const calendarResult = await this.ensureAx1Calendar(tokenResult.data);
      if (!calendarResult.success) {
        return { success: false, error: calendarResult.error };
      }
      
      // Process events sequentially to avoid rate limiting
      for (const event of events) {
        const result = await this.pushEventToGoogle(event);
        
        if (result.success) {
          results.push(result.data);
          
          switch (result.data.operation) {
            case 'created':
              created++;
              break;
            case 'updated':
              updated++;
              break;
            case 'skipped':
              skipped++;
              break;
            case 'error':
              errors++;
              break;
          }
        } else {
          errors++;
          results.push({
            ax1EventId: event.id,
            operation: 'error',
            message: result.error.message,
            timestamp: new Date().toISOString(),
          });
        }
        
        // Small delay to avoid rate limiting
        await this.delay(100);
      }
      
      const completedAt = new Date().toISOString();
      
      const batchResult: BatchSyncResult = {
        total: events.length,
        created,
        updated,
        skipped,
        errors,
        results,
        ax1CalendarId: this.ax1CalendarId || undefined,
        startedAt,
        completedAt,
      };
      
      this.log('info', 'syncEventsToGoogle', undefined, undefined,
        `Batch sync completed: ${created} created, ${updated} updated, ${skipped} skipped, ${errors} errors`);
      
      return { success: true, data: batchResult };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      this.log('error', 'syncEventsToGoogle', undefined, undefined, errorMessage);
      
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Batch sync failed',
          errorMessage,
          true
        ),
      };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: Calendar Management
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Gets the AX1 calendar ID in Google Calendar.
   * Creates the calendar if it doesn't exist.
   * 
   * @returns Calendar ID or error
   */
  public async getAx1CalendarId(): Promise<AsyncResult<string>> {
    const tokenResult = await this.authService.getValidAccessToken();
    if (!tokenResult.success) {
      return { success: false, error: tokenResult.error };
    }
    
    return this.ensureAx1Calendar(tokenResult.data);
  }

  /**
   * Gets the sync log entries.
   * 
   * @param limit - Maximum entries to return (default 100)
   * @returns Array of log entries (newest first)
   */
  public getSyncLog(limit = 100): SyncLogEntry[] {
    return this.syncLog.slice(-limit).reverse();
  }

  /**
   * Clears the sync log.
   */
  public clearSyncLog(): void {
    this.syncLog = [];
    this.log('info', 'clearSyncLog', undefined, undefined, 'Sync log cleared');
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: Pull Changes from Google
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Pulls changes from Google Calendar for synced AX1 events.
   * 
   * Rules:
   * - Only updates time and title from Google
   * - Never updates project, status, assignee, or ownership
   * - Deleted Google events are marked as 'cancelled' in AX1
   * - Conflicts are logged and returned for review, not silently resolved
   * 
   * @param ax1Events - AX1 events that have been synced to Google
   * @returns Pull result with updates and conflicts
   */
  public async pullGoogleCalendarChanges(
    ax1Events: CalendarEvent[]
  ): Promise<AsyncResult<PullChangesResult>> {
    const startedAt = new Date().toISOString();
    const results: PullEventResult[] = [];
    const allConflicts: SyncConflict[] = [];
    
    let updated = 0;
    let cancelled = 0;
    let skipped = 0;
    let conflictCount = 0;
    let errors = 0;
    
    // Filter to only events that have been synced to Google
    const syncedEvents = ax1Events.filter(
      e => e.externalSync?.provider === 'google' && e.externalSync?.externalEventId
    );
    
    this.log('info', 'pullGoogleCalendarChanges', undefined, undefined,
      `Starting pull for ${syncedEvents.length} synced events`);
    
    if (syncedEvents.length === 0) {
      return {
        success: true,
        data: {
          total: 0,
          updated: 0,
          cancelled: 0,
          skipped: 0,
          conflicts: 0,
          errors: 0,
          results: [],
          allConflicts: [],
          startedAt,
          completedAt: new Date().toISOString(),
        },
      };
    }
    
    try {
      // Ensure we're connected
      const tokenResult = await this.authService.getValidAccessToken();
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }
      
      // Process each synced event
      for (const ax1Event of syncedEvents) {
        const result = await this.pullSingleEventChanges(
          ax1Event,
          tokenResult.data
        );
        
        results.push(result);
        
        if (result.conflicts) {
          allConflicts.push(...result.conflicts);
        }
        
        switch (result.operation) {
          case 'updated':
            updated++;
            break;
          case 'cancelled':
            cancelled++;
            break;
          case 'skipped':
            skipped++;
            break;
          case 'conflict':
            conflictCount++;
            break;
          case 'error':
            errors++;
            break;
        }
        
        // Small delay to avoid rate limiting
        await this.delay(100);
      }
      
      const completedAt = new Date().toISOString();
      
      this.log('info', 'pullGoogleCalendarChanges', undefined, undefined,
        `Pull completed: ${updated} updated, ${cancelled} cancelled, ${skipped} skipped, ${conflictCount} conflicts, ${errors} errors`);
      
      return {
        success: true,
        data: {
          total: syncedEvents.length,
          updated,
          cancelled,
          skipped,
          conflicts: conflictCount,
          errors,
          results,
          allConflicts,
          startedAt,
          completedAt,
        },
      };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      this.log('error', 'pullGoogleCalendarChanges', undefined, undefined, errorMessage);
      
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Failed to pull changes from Google Calendar',
          errorMessage,
          true
        ),
      };
    }
  }

  /**
   * Gets pending conflicts that need user review.
   * 
   * @param ax1Events - Events to check for conflicts
   * @returns Array of conflicts requiring review
   */
  public getPendingConflicts(ax1Events: CalendarEvent[]): SyncConflict[] {
    return ax1Events
      .filter(e => e.externalSync?.hasPendingExternalChanges)
      .map(e => ({
        type: 'unknown_event' as ConflictType,
        ax1EventId: e.id,
        googleEventId: e.externalSync?.externalEventId || '',
        description: 'Event has pending external changes',
        recommendedAction: 'review' as const,
        autoResolved: false,
        detectedAt: e.externalSync?.lastSyncedAt || new Date().toISOString(),
      }));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Methods: Pull Changes
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Pulls changes for a single event from Google Calendar.
   */
  private async pullSingleEventChanges(
    ax1Event: CalendarEvent,
    accessToken: string
  ): Promise<PullEventResult> {
    const timestamp = new Date().toISOString();
    const googleEventId = ax1Event.externalSync!.externalEventId;
    const calendarId = ax1Event.externalSync!.externalCalendarId || this.ax1CalendarId || 'primary';
    
    try {
      // Fetch the Google event
      const response = await fetch(
        `${GOOGLE_CALENDAR_API}/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(googleEventId)}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      
      // Handle deleted event (404)
      if (response.status === 404) {
        this.log('warn', 'pullSingleEventChanges', ax1Event.id, googleEventId,
          'Google event not found - marking as cancelled');
        
        const conflict: SyncConflict = {
          type: 'event_deleted',
          ax1EventId: ax1Event.id,
          googleEventId,
          description: 'Event was deleted in Google Calendar',
          recommendedAction: 'accept',
          autoResolved: false,
          detectedAt: timestamp,
        };
        
        return {
          ax1EventId: ax1Event.id,
          googleEventId,
          operation: 'cancelled',
          partialUpdate: { status: 'cancelled' },
          conflicts: [conflict],
          message: 'Event deleted in Google - marked as cancelled',
          timestamp,
        };
      }
      
      if (!response.ok) {
        throw new Error(`Failed to fetch event: ${response.status}`);
      }
      
      const googleEvent: GoogleCalendarEvent = await response.json();
      
      // Check if cancelled in Google
      if (googleEvent.status === 'cancelled') {
        this.log('warn', 'pullSingleEventChanges', ax1Event.id, googleEventId,
          'Google event is cancelled');
        
        const conflict: SyncConflict = {
          type: 'event_cancelled',
          ax1EventId: ax1Event.id,
          googleEventId,
          description: 'Event was cancelled in Google Calendar',
          recommendedAction: 'accept',
          autoResolved: false,
          detectedAt: timestamp,
        };
        
        return {
          ax1EventId: ax1Event.id,
          googleEventId,
          operation: 'cancelled',
          partialUpdate: { status: 'cancelled' },
          conflicts: [conflict],
          message: 'Event cancelled in Google',
          timestamp,
        };
      }
      
      // Detect changes and conflicts
      const { partialUpdate, conflicts, hasAllowedChanges } = this.detectChanges(
        ax1Event,
        googleEvent,
        timestamp
      );
      
      // If there are conflicts on protected fields, return as conflict
      const protectedConflicts = conflicts.filter(c => c.type === 'protected_field_changed');
      if (protectedConflicts.length > 0) {
        this.log('warn', 'pullSingleEventChanges', ax1Event.id, googleEventId,
          `Protected field changes detected: ${protectedConflicts.map(c => c.field).join(', ')}`);
        
        return {
          ax1EventId: ax1Event.id,
          googleEventId,
          operation: 'conflict',
          conflicts,
          message: 'Protected fields were changed in Google - requires review',
          timestamp,
        };
      }
      
      // If no allowed changes, skip
      if (!hasAllowedChanges) {
        return {
          ax1EventId: ax1Event.id,
          googleEventId,
          operation: 'skipped',
          message: 'No changes detected',
          timestamp,
        };
      }
      
      // Return partial update for allowed fields only
      this.log('info', 'pullSingleEventChanges', ax1Event.id, googleEventId,
        `Changes detected: ${Object.keys(partialUpdate).join(', ')}`);
      
      return {
        ax1EventId: ax1Event.id,
        googleEventId,
        operation: 'updated',
        updatedFields: Object.keys(partialUpdate),
        partialUpdate,
        conflicts: conflicts.length > 0 ? conflicts : undefined,
        message: `Updated fields: ${Object.keys(partialUpdate).join(', ')}`,
        timestamp,
      };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      this.log('error', 'pullSingleEventChanges', ax1Event.id, googleEventId, errorMessage);
      
      return {
        ax1EventId: ax1Event.id,
        googleEventId,
        operation: 'error',
        message: errorMessage,
        timestamp,
      };
    }
  }

  /**
   * Detects changes between AX1 event and Google event.
   * Only allows updates to time and title.
   * Logs conflicts for protected fields.
   */
  private detectChanges(
    ax1Event: CalendarEvent,
    googleEvent: GoogleCalendarEvent,
    timestamp: string
  ): {
    partialUpdate: Partial<CalendarEvent>;
    conflicts: SyncConflict[];
    hasAllowedChanges: boolean;
  } {
    const partialUpdate: Partial<CalendarEvent> = {};
    const conflicts: SyncConflict[] = [];
    
    // Parse Google event title (remove emoji prefix if present)
    const googleTitle = this.parseGoogleTitle(googleEvent.summary);
    
    // Check title change (ALLOWED)
    if (googleTitle !== ax1Event.title) {
      partialUpdate.title = googleTitle;
      
      conflicts.push({
        type: 'title_changed',
        ax1EventId: ax1Event.id,
        googleEventId: googleEvent.id || '',
        field: 'title',
        ax1Value: ax1Event.title,
        googleValue: googleTitle,
        description: `Title changed from "${ax1Event.title}" to "${googleTitle}"`,
        recommendedAction: 'accept',
        autoResolved: true,
        detectedAt: timestamp,
      });
      
      this.log('info', 'detectChanges', ax1Event.id, googleEvent.id,
        `Title change detected: "${ax1Event.title}" → "${googleTitle}"`);
    }
    
    // Check time/date change (ALLOWED)
    const { date, time, duration } = this.parseGoogleDateTime(googleEvent);
    const ax1DateStr = new Date(ax1Event.date).toISOString().split('T')[0];
    const googleDateStr = date.toISOString().split('T')[0];
    
    if (ax1DateStr !== googleDateStr) {
      partialUpdate.date = date;
      
      conflicts.push({
        type: 'time_changed',
        ax1EventId: ax1Event.id,
        googleEventId: googleEvent.id || '',
        field: 'date',
        ax1Value: ax1DateStr,
        googleValue: googleDateStr,
        description: `Date changed from ${ax1DateStr} to ${googleDateStr}`,
        recommendedAction: 'accept',
        autoResolved: true,
        detectedAt: timestamp,
      });
    }
    
    if (time !== ax1Event.time) {
      partialUpdate.time = time;
      
      conflicts.push({
        type: 'time_changed',
        ax1EventId: ax1Event.id,
        googleEventId: googleEvent.id || '',
        field: 'time',
        ax1Value: ax1Event.time,
        googleValue: time,
        description: `Time changed from ${ax1Event.time || 'all-day'} to ${time || 'all-day'}`,
        recommendedAction: 'accept',
        autoResolved: true,
        detectedAt: timestamp,
      });
    }
    
    if (duration !== undefined && duration !== ax1Event.duration) {
      partialUpdate.duration = duration;
      
      conflicts.push({
        type: 'time_changed',
        ax1EventId: ax1Event.id,
        googleEventId: googleEvent.id || '',
        field: 'duration',
        ax1Value: ax1Event.duration,
        googleValue: duration,
        description: `Duration changed from ${ax1Event.duration || 1}h to ${duration}h`,
        recommendedAction: 'accept',
        autoResolved: true,
        detectedAt: timestamp,
      });
    }
    
    // Check location change (ALLOWED - informational only)
    if (googleEvent.location !== ax1Event.location) {
      partialUpdate.location = googleEvent.location;
    }
    
    // Check for protected field changes (NOT ALLOWED - log conflict only)
    // We can detect if someone edited the description to change project info
    // by parsing the extended properties we set
    const extProps = googleEvent.extendedProperties?.private;
    if (extProps) {
      // Check if project was changed (PROTECTED)
      if (extProps.ax1ProjectId && extProps.ax1ProjectId !== (ax1Event.project || '')) {
        conflicts.push({
          type: 'protected_field_changed',
          ax1EventId: ax1Event.id,
          googleEventId: googleEvent.id || '',
          field: 'project',
          ax1Value: ax1Event.project,
          googleValue: extProps.ax1ProjectId,
          description: 'Project cannot be changed from Google Calendar',
          recommendedAction: 'reject',
          autoResolved: false,
          detectedAt: timestamp,
        });
        
        this.log('warn', 'detectChanges', ax1Event.id, googleEvent.id,
          'Protected field change attempted: project');
      }
      
      // Check if event type was changed (PROTECTED)
      if (extProps.ax1EventType && extProps.ax1EventType !== ax1Event.type) {
        conflicts.push({
          type: 'protected_field_changed',
          ax1EventId: ax1Event.id,
          googleEventId: googleEvent.id || '',
          field: 'type',
          ax1Value: ax1Event.type,
          googleValue: extProps.ax1EventType,
          description: 'Event type cannot be changed from Google Calendar',
          recommendedAction: 'reject',
          autoResolved: false,
          detectedAt: timestamp,
        });
        
        this.log('warn', 'detectChanges', ax1Event.id, googleEvent.id,
          'Protected field change attempted: type');
      }
    }
    
    // Update sync info with last modified time
    partialUpdate.externalSync = {
      ...ax1Event.externalSync!,
      lastSyncedAt: timestamp,
      externalLastModified: googleEvent.updated,
      hasPendingExternalChanges: conflicts.some(c => !c.autoResolved),
      syncStatus: conflicts.some(c => !c.autoResolved) ? 'conflict' : 'synced',
    };
    
    return {
      partialUpdate,
      conflicts,
      hasAllowedChanges: Object.keys(partialUpdate).length > 1, // More than just externalSync
    };
  }

  /**
   * Parses Google event title, removing emoji prefix added by AX1.
   */
  private parseGoogleTitle(summary: string): string {
    // Remove emoji prefixes we added (📅 for meeting, 🎯 for milestone)
    // Use alternation instead of character class for emoji (avoids surrogate pair issues)
    return summary.replace(/^(📅|🎯)\s*/u, '').trim();
  }

  /**
   * Parses Google event start/end into date, time, and duration.
   */
  private parseGoogleDateTime(googleEvent: GoogleCalendarEvent): {
    date: Date;
    time?: string;
    duration?: number;
  } {
    const start = googleEvent.start;
    const end = googleEvent.end;
    
    if (start.dateTime) {
      // Timed event
      const startDate = new Date(start.dateTime);
      const endDate = end.dateTime ? new Date(end.dateTime) : startDate;
      
      const hours = startDate.getHours().toString().padStart(2, '0');
      const minutes = startDate.getMinutes().toString().padStart(2, '0');
      const time = `${hours}:${minutes}`;
      
      const durationMs = endDate.getTime() - startDate.getTime();
      const duration = Math.round(durationMs / (1000 * 60 * 60)); // Hours
      
      return {
        date: startDate,
        time,
        duration: duration > 0 ? duration : 1,
      };
    } else if (start.date) {
      // All-day event
      return {
        date: new Date(start.date),
        time: undefined,
        duration: undefined,
      };
    }
    
    // Fallback
    return {
      date: new Date(),
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Methods: Google Calendar API
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Ensures the AX1 calendar exists in Google Calendar.
   * Creates it if not found.
   */
  private async ensureAx1Calendar(accessToken: string): Promise<AsyncResult<string>> {
    // Return cached ID if available
    if (this.ax1CalendarId) {
      return { success: true, data: this.ax1CalendarId };
    }
    
    try {
      // First, look for existing AX1 calendar
      const listResponse = await fetch(
        `${GOOGLE_CALENDAR_API}/users/me/calendarList`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      
      if (!listResponse.ok) {
        throw new Error(`Failed to list calendars: ${listResponse.status}`);
      }
      
      const listData = await listResponse.json();
      const calendars: GoogleCalendarListEntry[] = listData.items || [];
      
      // Look for existing AX1 calendar
      const existingCalendar = calendars.find(
        cal => cal.summary === AX1_CALENDAR_NAME
      );
      
      if (existingCalendar) {
        this.ax1CalendarId = existingCalendar.id;
        this.log('info', 'ensureAx1Calendar', undefined, undefined,
          `Found existing AX1 calendar: ${existingCalendar.id}`);
        return { success: true, data: existingCalendar.id };
      }
      
      // Create new AX1 calendar
      const createResponse = await fetch(
        `${GOOGLE_CALENDAR_API}/calendars`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            summary: AX1_CALENDAR_NAME,
            description: AX1_CALENDAR_DESCRIPTION,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        }
      );
      
      if (!createResponse.ok) {
        throw new Error(`Failed to create calendar: ${createResponse.status}`);
      }
      
      const newCalendar = await createResponse.json();
      this.ax1CalendarId = newCalendar.id;
      
      this.log('info', 'ensureAx1Calendar', undefined, undefined,
        `Created new AX1 calendar: ${newCalendar.id}`);
      
      return { success: true, data: newCalendar.id };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      this.log('error', 'ensureAx1Calendar', undefined, undefined, errorMessage);
      
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Failed to access or create AX1 calendar',
          errorMessage,
          true
        ),
      };
    }
  }

  /**
   * Creates a new event in Google Calendar.
   */
  private async createGoogleEventInternal(
    event: CalendarEvent,
    calendarId: string,
    accessToken: string
  ): Promise<SyncEventResult> {
    const timestamp = new Date().toISOString();
    
    try {
      const googleEvent = convertToGoogleEvent(event, event.id);
      
      const response = await fetch(
        `${GOOGLE_CALENDAR_API}/calendars/${encodeURIComponent(calendarId)}/events`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(googleEvent),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}`);
      }
      
      const createdEvent = await response.json();
      
      const syncInfo: ExternalSyncInfo = {
        provider: 'google',
        externalEventId: createdEvent.id,
        externalCalendarId: calendarId,
        syncDirection: 'ax1_to_external',
        lastSyncedAt: timestamp,
        externalLastModified: createdEvent.updated,
        syncStatus: 'synced',
      };
      
      this.log('info', 'createGoogleEvent', event.id, createdEvent.id,
        `Created event: ${event.title}`);
      
      return {
        ax1EventId: event.id,
        googleEventId: createdEvent.id,
        operation: 'created',
        syncInfo,
        timestamp,
      };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      this.log('error', 'createGoogleEvent', event.id, undefined, errorMessage);
      
      return {
        ax1EventId: event.id,
        operation: 'error',
        message: errorMessage,
        timestamp,
      };
    }
  }

  /**
   * Updates an existing event in Google Calendar.
   */
  private async updateGoogleEventInternal(
    event: CalendarEvent,
    calendarId: string,
    googleEventId: string,
    accessToken: string
  ): Promise<SyncEventResult> {
    const timestamp = new Date().toISOString();
    
    try {
      const googleEvent = convertToGoogleEvent(event, event.id);
      
      const response = await fetch(
        `${GOOGLE_CALENDAR_API}/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(googleEventId)}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(googleEvent),
        }
      );
      
      if (!response.ok) {
        // If event not found (404), it may have been deleted in Google
        // We should create a new one
        if (response.status === 404) {
          this.log('warn', 'updateGoogleEvent', event.id, googleEventId,
            'Event not found in Google, creating new one');
          return this.createGoogleEventInternal(event, calendarId, accessToken);
        }
        
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}`);
      }
      
      const updatedEvent = await response.json();
      
      const syncInfo: ExternalSyncInfo = {
        provider: 'google',
        externalEventId: updatedEvent.id,
        externalCalendarId: calendarId,
        syncDirection: 'ax1_to_external',
        lastSyncedAt: timestamp,
        externalLastModified: updatedEvent.updated,
        syncStatus: 'synced',
      };
      
      this.log('info', 'updateGoogleEvent', event.id, updatedEvent.id,
        `Updated event: ${event.title}`);
      
      return {
        ax1EventId: event.id,
        googleEventId: updatedEvent.id,
        operation: 'updated',
        syncInfo,
        timestamp,
      };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      this.log('error', 'updateGoogleEvent', event.id, googleEventId, errorMessage);
      
      return {
        ax1EventId: event.id,
        googleEventId,
        operation: 'error',
        message: errorMessage,
        timestamp,
      };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Methods: Logging
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Adds an entry to the sync log.
   */
  private log(
    level: SyncLogEntry['level'],
    operation: string,
    eventId?: string,
    googleEventId?: string,
    message?: string,
    details?: Record<string, unknown>
  ): void {
    const entry: SyncLogEntry = {
      timestamp: new Date().toISOString(),
      level,
      operation,
      eventId,
      googleEventId,
      message: message || '',
      details,
    };
    
    this.syncLog.push(entry);
    
    // Trim log if too large
    if (this.syncLog.length > this.MAX_LOG_ENTRIES) {
      this.syncLog = this.syncLog.slice(-this.MAX_LOG_ENTRIES);
    }
    
    // Also log to console in development
    const consoleMethod = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
    consoleMethod(`[GoogleSync] ${operation}: ${message}`, { eventId, googleEventId, details });
  }

  /**
   * Simple delay helper.
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience Functions (Facade)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pushes a single AX1 event to Google Calendar.
 * 
 * @param event - AX1 calendar event to sync
 * @returns Sync result with updated sync info
 */
export async function pushAx1EventToGoogle(
  event: CalendarEvent
): Promise<AsyncResult<SyncEventResult>> {
  const service = GoogleCalendarSyncService.getInstance();
  return service.pushEventToGoogle(event);
}

/**
 * Updates an existing event in Google Calendar.
 * 
 * @param event - AX1 calendar event with updated data
 * @returns Sync result
 */
export async function updateGoogleEvent(
  event: CalendarEvent
): Promise<AsyncResult<SyncEventResult>> {
  const service = GoogleCalendarSyncService.getInstance();
  return service.updateGoogleEvent(event);
}

/**
 * Syncs multiple AX1 events to Google Calendar.
 * 
 * @param events - Array of AX1 events to sync
 * @returns Batch sync result
 */
export async function syncEventsToGoogle(
  events: CalendarEvent[]
): Promise<AsyncResult<BatchSyncResult>> {
  const service = GoogleCalendarSyncService.getInstance();
  return service.syncEventsToGoogle(events);
}

/**
 * Gets the sync log entries.
 * 
 * @param limit - Maximum entries to return
 * @returns Array of log entries
 */
export function getGoogleSyncLog(limit?: number): SyncLogEntry[] {
  const service = GoogleCalendarSyncService.getInstance();
  return service.getSyncLog(limit);
}

/**
 * Pulls changes from Google Calendar for synced AX1 events.
 * 
 * Rules:
 * - Only updates time and title from Google
 * - Never updates project, status, assignee, or ownership
 * - Deleted Google events are marked as 'cancelled' in AX1
 * - Conflicts are logged and returned for review
 * 
 * @param ax1Events - AX1 events that have been synced to Google
 * @returns Pull result with updates and conflicts
 */
export async function pullGoogleCalendarChanges(
  ax1Events: CalendarEvent[]
): Promise<AsyncResult<PullChangesResult>> {
  const service = GoogleCalendarSyncService.getInstance();
  return service.pullGoogleCalendarChanges(ax1Events);
}

/**
 * Gets pending conflicts that need user review.
 * 
 * @param ax1Events - Events to check for conflicts
 * @returns Array of conflicts requiring review
 */
export function getGooglePendingConflicts(ax1Events: CalendarEvent[]): SyncConflict[] {
  const service = GoogleCalendarSyncService.getInstance();
  return service.getPendingConflicts(ax1Events);
}

// Default export
export default GoogleCalendarSyncService;
